/**
 * StaffCortexPermissions — IT-SCE Intern: Cortex Standing-Permissions
 * verwalten (allow / deny / confirm).
 *
 * TQ #9698 (Staff-Migration Teil E), portiert von
 * customer-portal/src/pages/staff/StaffCortexPermissions.tsx. Backend
 * portal-api /api/staff/cortex/permissions (services/api.ts
 * staffListCortexPermissions/staffCreateCortexPermission/
 * staffDeleteCortexPermission, proxied zu Cortex admin-API).
 *
 * SICHERHEITSHINWEIS: Diese Seite ist ein reiner Client fuer bereits
 * live existierende, staff-auth-gated admin-api-Endpunkte (requireStaff()
 * Middleware in portal-api + CORTEX_ADMIN_TOKEN service-token) — kein
 * Cortex-authz-Rust-Code wird durch diese Migration veraendert.
 */

import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type { StaffCortexPermission } from '../services/api'
import { useToast } from '../components/ui'

const TYPE_COLORS: Record<string, string> = {
  allow: '#d1fae5',
  deny: '#fee2e2',
  confirm: '#fef3c7',
}
const TYPE_TEXT: Record<string, string> = {
  allow: '#065f46',
  deny: '#991b1b',
  confirm: '#92400e',
}

function Badge({ type }: { type: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      background: TYPE_COLORS[type] ?? '#f3f4f6',
      color: TYPE_TEXT[type] ?? '#374151',
    }}>
      {type}
    </span>
  )
}

interface CreateForm {
  user_id: string
  permission_type: string
  scope: string
  target: string
  expires_at: string
}

const EMPTY_FORM: CreateForm = {
  user_id: '',
  permission_type: 'allow',
  scope: 'tool',
  target: '',
  expires_at: '',
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const { showToast } = useToast()

  const set = (k: keyof CreateForm, v: string) => setForm(f => ({ ...f, [k]: v }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.user_id.trim() || !form.target.trim()) return
    setSaving(true)
    try {
      await api.staffCreateCortexPermission(form.user_id.trim(), {
        permission_type: form.permission_type,
        scope: form.scope,
        target: form.target.trim(),
        expires_at: form.expires_at || undefined,
      })
      showToast('success', 'Permission angelegt')
      onCreated()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
      showToast('error', `Fehler: ${msg}`)
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const,
  }
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28, width: 480,
        maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>
          Neue Permission
        </h2>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>User-ID (UUID)</label>
            <input style={inputStyle} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={form.user_id} onChange={e => set('user_id', e.target.value)} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Typ</label>
              <select style={inputStyle} value={form.permission_type}
                onChange={e => set('permission_type', e.target.value)}>
                <option value="allow">allow</option>
                <option value="deny">deny</option>
                <option value="confirm">confirm</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Scope</label>
              <select style={inputStyle} value={form.scope}
                onChange={e => set('scope', e.target.value)}>
                <option value="tool">tool</option>
                <option value="server">server</option>
                <option value="category">category</option>
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Target</label>
            <input style={inputStyle} placeholder="z.B. bash_execute oder filesystem"
              value={form.target} onChange={e => set('target', e.target.value)} required />
          </div>
          <div>
            <label style={labelStyle}>Läuft ab (optional, RFC3339)</label>
            <input type="datetime-local" style={inputStyle}
              onChange={e => set('expires_at', e.target.value ? new Date(e.target.value).toISOString() : '')} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
              Abbrechen
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: '8px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {saving ? 'Speichern…' : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function StaffCortexPermissions() {
  const [perms, setPerms] = useState<StaffCortexPermission[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const { showToast } = useToast()

  async function load() {
    setLoading(true)
    try {
      const data = await api.staffListCortexPermissions()
      setPerms(data)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
      showToast('error', `Laden fehlgeschlagen: ${msg}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function revoke(p: StaffCortexPermission) {
    if (!confirm(`Permission "${p.permission_type} ${p.scope}:${p.target}" widerrufen?`)) return
    try {
      await api.staffDeleteCortexPermission(p.user_id, p.id)
      showToast('success', 'Permission widerrufen')
      setPerms(ps => ps.filter(x => x.id !== p.id))
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
      showToast('error', `Fehler: ${msg}`)
    }
  }

  const q = search.toLowerCase()
  const filtered = perms.filter(p =>
    !q ||
    p.user_id.includes(q) ||
    p.target.toLowerCase().includes(q) ||
    p.permission_type.includes(q) ||
    p.scope.includes(q) ||
    p.granted_via.toLowerCase().includes(q),
  )

  const counts = { allow: 0, deny: 0, confirm: 0 }
  for (const p of perms) { if (p.permission_type in counts) counts[p.permission_type as keyof typeof counts]++ }

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>🔒 Standing Permissions</h1>
          <p className="page-subtitle">
            Stehende Tool-Permissions verwalten (allow / deny / confirm)
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}
        >
          + Neue Permission
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {(['allow', 'deny', 'confirm'] as const).map(t => (
          <div key={t} style={{ padding: '8px 16px', background: TYPE_COLORS[t], borderRadius: 8 }}>
            <span style={{ color: TYPE_TEXT[t], fontWeight: 700, fontSize: 14 }}>
              {counts[t]} {t}
            </span>
          </div>
        ))}
        <div style={{ padding: '8px 16px', background: '#f3f4f6', borderRadius: 8, color: '#374151', fontWeight: 700, fontSize: 14 }}>
          {perms.length} gesamt
        </div>
      </div>

      <input
        placeholder="Suche nach User-ID, Target, Typ, Quelle…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
      />

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Lade Permissions…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
          {search ? 'Keine Ergebnisse für diese Suche.' : 'Keine Permissions vorhanden.'}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>User-ID</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Typ</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Scope</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Target</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Quelle</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Angelegt</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Läuft ab</th>
                <th style={{ padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 11 }}>
                    {p.user_id.slice(0, 8)}…
                  </td>
                  <td style={{ padding: '10px 12px' }}><Badge type={p.permission_type} /></td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>{p.scope}</td>
                  <td style={{ padding: '10px 12px' }}><code style={{ fontSize: 12 }}>{p.target}</code></td>
                  <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 12 }}>{p.granted_via}</td>
                  <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: 12 }}>
                    {new Date(p.granted_at).toLocaleDateString('de-DE')}
                  </td>
                  <td style={{ padding: '10px 12px', color: p.expires_at ? '#92400e' : '#9ca3af', fontSize: 12 }}>
                    {p.expires_at ? new Date(p.expires_at).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      onClick={() => revoke(p)}
                      style={{ padding: '4px 10px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                    >
                      Widerrufen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); void load() }}
        />
      )}
    </div>
  )
}
